import { ApiError, createEventTemplate, listEventTemplates } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { CreateEventTemplateRequest, EventTemplateResponse } from '@jq4race/shared';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAuthState } from '../../../auth/ui/auth-state-context';
import { isTemplateCreateAllowed } from '../constants';
import { CreateEventTemplateForm } from './CreateEventTemplateForm';
import { EventTemplatesTable } from './EventTemplatesTable';

const FEEDBACK_TIMEOUT_MS = 5000;

// R1.7/R1.8 — reusable event templates. Create is org_admin-only at the
// backend (AdminEventTemplatesController.create() → requireOrgAdminActor);
// mirrored here by reading the current user's role (same idiom as
// CloseEventAction's force-close gate), NOT by hiding a 403 after the fact.
export function EventTemplatesSection() {
  const { t } = useTranslation('events');
  const { session } = useAuthState();
  const canCreate = isTemplateCreateAllowed(session?.user.role);

  const [templates, setTemplates] = useState<EventTemplateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listEventTemplates();
      setTemplates(res.templates);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('eventTemplatesSection.loadError') })
          : t('eventTemplatesSection.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!success && !error) return;
    const id = window.setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, FEEDBACK_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [success, error]);

  async function handleCreate(values: CreateEventTemplateRequest) {
    try {
      const created = await createEventTemplate(values);
      setSuccess(t('eventTemplatesSection.createdSuccess', { name: created.name }));
      await reload();
    } catch (e) {
      throw e instanceof ApiError ? new LocalizedError(translateApiError(e, t)) : e;
    }
  }

  return (
    <div className="space-y-6">
      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      {canCreate ? (
        <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-4 sm:p-6">
          <CreateEventTemplateForm onSubmit={handleCreate} />
        </div>
      ) : (
        <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3 text-[13px] text-jq-graphite">
          {t('eventTemplatesSection.nonOrgAdminNote')}
        </div>
      )}

      <EventTemplatesTable templates={templates} loading={loading} />
    </div>
  );
}

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('events');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
